// src/components/PredictionLab.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import { getFundamentals, getPrice, getAaaCorporateYield, getCompanyLogo } from "../services/api";
import { postPredictLgbm, postPredictLgbmRegression } from "../services/mlApi";
import { computeCorrelationsForFundamentals, parseAaaYieldApiResponse } from "../utils/fundamentalMetrics";
import { API_BASE_URL } from "../config/apiConfig";
import { companyLogoTitleStyle, COMPANY_LOGO_TITLE_SIZE } from "../utils/chartCompanyLogo";

const BRAND = {
  text: "#e8e8f0",
  textSecondary: "#b8b8c8",
  muted: "#888898",
  accent: "#6366f1",
  accentHover: "#818cf8",
  border: "#2a2a3a",
  bg: "#0a0a0f",
  panel: "#1a1a24",
};

const HORIZONS = [
  { key: "30", label: "1 month" },
  { key: "90", label: "1 quarter" },
  { key: "365", label: "1 year" },
];

function priceToPoints(priceData) {
  if (!Array.isArray(priceData) || !priceData.length) return [];
  return priceData
    .filter((r) => r.date && (r.close != null || r.adjClose != null))
    .map((r) => {
      const [Y, M, D] = r.date.split("-").map(Number);
      const time = new Date(Y, M - 1, D).getTime() / 1000;
      const value = parseFloat(r.close ?? r.adjClose);
      return { time, value };
    })
    .sort((a, b) => a.time - b.time);
}

function toEpoch(ymd) {
  const [Y, M, D] = ymd.split("-").map(Number);
  return Date.UTC(Y, M - 1, D) / 1000;
}

export default function PredictionLab() {
  const [symbol, setSymbol] = useState("AAPL");
  const [fundamentals, setFundamentals] = useState(null);
  const [pricePoints, setPricePoints] = useState([]);
  const [correlations, setCorrelations] = useState([]);
  const [correlationsLoading, setCorrelationsLoading] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState(new Set(["revenue", "eps"]));
  const [predictionResult, setPredictionResult] = useState(null);
  const [regressionResult, setRegressionResult] = useState(null);
  const [predictLoading, setPredictLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("all");
  const [usePriceAction, setUsePriceAction] = useState(true);
  const [isLog, setIsLog] = useState(false);
  const [tickerOptions, setTickerOptions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestHighlight, setSuggestHighlight] = useState(0);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  const chartWrapRef = useRef(null);
  const chartRef = useRef(null);
  const histRef = useRef(null);
  const btRef = useRef(null);
  const fcRef = useRef(null);
  const btLoRef = useRef(null);
  const btHiRef = useRef(null);
  const fcLoRef = useRef(null);
  const fcHiRef = useRef(null);
  const searchBoxRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const sym = (symbol || "").trim().toUpperCase();
    setCompanyLogoUrl(null);
    if (!sym) return undefined;
    getCompanyLogo(sym).then((url) => {
      if (!cancelled) setCompanyLogoUrl(url);
    });
    return () => { cancelled = true; };
  }, [symbol]);

  useEffect(() => {
    let mounted = true;
    setSuggestLoading(true);
    (async () => {
      try {
        const [tickersRes, spRes] = await Promise.all([
          fetch(`${API_BASE_URL}/tickers?ex=NYSE,NASDAQ,AMEX,TSX`),
          fetch(`${API_BASE_URL}/sp500`),
        ]);
        const [tickers, sp500] = await Promise.all([
          tickersRes.ok ? tickersRes.json() : [],
          spRes.ok ? spRes.json() : [],
        ]);
        const map = new Map();
        [...(Array.isArray(tickers) ? tickers : []), ...(Array.isArray(sp500) ? sp500 : [])].forEach((r) => {
          const sym = String(r?.symbol || "").toUpperCase();
          if (!sym) return;
          const name = r?.name || r?.companyName || "";
          if (!map.has(sym)) map.set(sym, { symbol: sym, name });
        });
        if (mounted) setTickerOptions(Array.from(map.values()).sort((a, b) => a.symbol.localeCompare(b.symbol)));
      } catch {
        if (mounted) setTickerOptions([]);
      } finally {
        if (mounted) setSuggestLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const loadData = useCallback(async () => {
    const sym = (symbol || "").trim().toUpperCase();
    if (!sym) return;
    setError(null);
    // Reset old outputs so chart/predictions always match the newly loaded ticker.
    setPredictionResult(null);
    setRegressionResult(null);
    setCorrelationsLoading(true);
    setCorrelations([]);
    setFundamentals(null);
    setPricePoints([]);
    try {
      const [fundRes, priceRes, aaaRes] = await Promise.all([
        getFundamentals(sym),
        getPrice(sym, { timeseries: 10000, withVolume: false }),
        getAaaCorporateYield().catch(() => ({ points: [], fallbackPct: 5 })),
      ]);
      const points = priceToPoints(priceRes || []);
      const aaaParsed = parseAaaYieldApiResponse(aaaRes);
      setFundamentals(fundRes || null);
      setPricePoints(points);
      if (fundRes && points.length >= 2) {
        const corr = computeCorrelationsForFundamentals(fundRes, points, {
          interval: "quarter",
          lagDays: 45,
          aaaYieldPoints: aaaParsed.points,
          fallbackYieldPercent: aaaParsed.fallbackPct,
        });
        setCorrelations(corr);
      }
    } catch (e) {
      setError(e?.message || "Failed to load data");
      setCorrelations([]);
    } finally {
      setCorrelationsLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleMetric = (metricKey) => {
    setSelectedMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(metricKey)) next.delete(metricKey);
      else next.add(metricKey);
      return next;
    });
  };

  const runPrediction = async () => {
    const sym = (symbol || "").trim().toUpperCase();
    if (!sym) {
      setError("Enter a symbol");
      return;
    }
    setError(null);
    setPredictionResult(null);
    setPredictLoading(true);
    try {
      const result = await postPredictLgbm({
        symbol: sym,
        selected_features: Array.from(selectedMetrics),
      });
      setPredictionResult(result);
    } catch (e) {
      setError(e?.message || "Prediction failed");
    } finally {
      setPredictLoading(false);
    }
  };

  const runRegression = async () => {
    const sym = (symbol || "").trim().toUpperCase();
    if (!sym) return;
    setPredictLoading(true);
    setError(null);
    try {
      const res = await postPredictLgbmRegression({
        symbol: sym,
        selected_features: Array.from(selectedMetrics),
        forecast_days: 252,
        use_price_action: usePriceAction,
      });
      setRegressionResult(res);
    } catch (e) {
      setError(e?.message || "Regression prediction failed");
    } finally {
      setPredictLoading(false);
    }
  };

  const sortedCorrelations = [...correlations].sort((a, b) => {
    const va = a[sortBy] != null ? Math.abs(a[sortBy]) : -1;
    const vb = b[sortBy] != null ? Math.abs(b[sortBy]) : -1;
    return vb - va;
  });

  const filteredSuggestions = useMemo(() => {
    const q = (symbol || "").trim().toLowerCase();
    if (!q) return tickerOptions.slice(0, 20);
    return tickerOptions
      .filter((o) => o.symbol.toLowerCase().includes(q) || (o.name || "").toLowerCase().includes(q))
      .slice(0, 20);
  }, [symbol, tickerOptions]);

  const pickSuggestion = (idx) => {
    const it = filteredSuggestions[idx];
    if (!it) return;
    setSymbol(it.symbol);
    setSuggestOpen(false);
    setSuggestHighlight(0);
    // Load chosen ticker immediately.
    setTimeout(() => {
      loadData();
    }, 0);
  };

  const historySeries = useMemo(() => {
    const fromRegression = (regressionResult?.history || [])
      .map((p) => ({ time: toEpoch(p.ds), value: Number(p.value) }))
      .filter((p) => Number.isFinite(p.value));
    if (fromRegression.length) return fromRegression;
    // Always show loaded historical price even before/without regression response.
    return (pricePoints || []).map((p) => ({ time: p.time, value: Number(p.value) })).filter((p) => Number.isFinite(p.value));
  }, [regressionResult, pricePoints]);
  const btSeries = useMemo(
    () => (regressionResult?.backtest || []).map((p) => ({ time: toEpoch(p.ds), value: Number(p.yhat), lo: Number(p.lower), hi: Number(p.upper) })).filter((p) => Number.isFinite(p.value)),
    [regressionResult]
  );
  const fcSeries = useMemo(
    () => (regressionResult?.forecast || []).map((p) => ({ time: toEpoch(p.ds), value: Number(p.yhat), lo: Number(p.lower), hi: Number(p.upper) })).filter((p) => Number.isFinite(p.value)),
    [regressionResult]
  );

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el) return;
    let disposed = false;
    const chart = createChart(el, {
      width: el.clientWidth,
      height: 430,
      layout: { background: { type: "solid", color: BRAND.panel }, textColor: BRAND.textSecondary },
      grid: { vertLines: { color: BRAND.border }, horzLines: { color: BRAND.border } },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false },
    });
    histRef.current = chart.addLineSeries({ color: "#94a3b8", lineWidth: 2, title: "Price" });
    btRef.current = chart.addLineSeries({ color: "#f59e0b", lineWidth: 2, title: "Backtest prediction" });
    fcRef.current = chart.addLineSeries({ color: "#22c55e", lineWidth: 2, title: "Forecast" });
    btLoRef.current = chart.addLineSeries({ color: "rgba(245,158,11,0.45)", lineWidth: 1 });
    btHiRef.current = chart.addLineSeries({ color: "rgba(245,158,11,0.45)", lineWidth: 1 });
    fcLoRef.current = chart.addLineSeries({ color: "rgba(34,197,94,0.45)", lineWidth: 1 });
    fcHiRef.current = chart.addLineSeries({ color: "rgba(34,197,94,0.45)", lineWidth: 1 });
    chartRef.current = chart;

    const ro = new ResizeObserver((entries) => {
      if (disposed) return;
      for (const entry of entries) {
        try {
          chart.applyOptions({ width: entry.contentRect.width });
        } catch {
          // Ignore updates after chart disposal (React refresh / StrictMode double-invoke).
        }
      }
    });
    ro.observe(el);
    return () => {
      disposed = true;
      ro.disconnect();
      // Clear refs first so data effect doesn't write to disposed series.
      chartRef.current = null;
      histRef.current = null;
      btRef.current = null;
      fcRef.current = null;
      btLoRef.current = null;
      btHiRef.current = null;
      fcLoRef.current = null;
      fcHiRef.current = null;
      try {
        chart.remove();
      } catch {
        // Ignore double-dispose during hot reload.
      }
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;
    try {
      chartRef.current.applyOptions({
        rightPriceScale: {
          borderVisible: false,
          mode: isLog ? 1 : 0, // 1 = logarithmic, 0 = normal
        },
      });
    } catch {
      // Ignore stale chart during hot-reload.
    }
  }, [isLog]);

  useEffect(() => {
    if (!chartRef.current || !histRef.current) return;
    try {
      histRef.current.setData(historySeries);
      btRef.current?.setData(btSeries.map((p) => ({ time: p.time, value: p.value })));
      btLoRef.current?.setData(btSeries.map((p) => ({ time: p.time, value: p.lo })));
      btHiRef.current?.setData(btSeries.map((p) => ({ time: p.time, value: p.hi })));
      fcRef.current?.setData(fcSeries.map((p) => ({ time: p.time, value: p.value })));
      fcLoRef.current?.setData(fcSeries.map((p) => ({ time: p.time, value: p.lo })));
      fcHiRef.current?.setData(fcSeries.map((p) => ({ time: p.time, value: p.hi })));
      chartRef.current.timeScale().fitContent();
    } catch {
      // Ignore stale writes during hot module replacement.
    }
  }, [historySeries, btSeries, fcSeries]);

  return (
    <div
      style={{
        maxWidth: 960,
        margin: "0 auto",
        padding: 24,
        color: BRAND.text,
      }}
    >
      <h1
        style={{
          margin: "0 0 8px",
          fontSize: "clamp(1.5rem, 3vw, 2rem)",
          fontWeight: 700,
          letterSpacing: "-0.02em",
        }}
      >
        Prediction Lab
      </h1>
      <p style={{ margin: "0 0 24px", color: BRAND.textSecondary, fontSize: 14 }}>
        Use fundamentals, price, and date to predict the probability of future price action (LightGBM). Select
        fundamentals below using the same correlation view as the Fundamentals tab.
      </p>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 24 }}>
        <label ref={searchBoxRef} style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
          <span style={{ color: BRAND.textSecondary, fontSize: 14 }}>Symbol</span>
          <input
            type="text"
            value={symbol}
            onChange={(e) => {
              setSymbol(e.target.value.toUpperCase());
              setSuggestOpen(true);
              setSuggestHighlight(0);
            }}
            onFocus={() => setSuggestOpen(true)}
            onBlur={() => setTimeout(() => setSuggestOpen(false), 120)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setSuggestOpen(true);
                setSuggestHighlight((h) => Math.min(h + 1, Math.max(filteredSuggestions.length - 1, 0)));
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setSuggestHighlight((h) => Math.max(0, h - 1));
                return;
              }
              if (e.key === "Enter") {
                e.preventDefault();
                if (suggestOpen && filteredSuggestions.length) pickSuggestion(suggestHighlight);
                else loadData();
              }
            }}
            placeholder="e.g. AAPL"
            style={{
              width: 100,
              padding: "8px 10px",
              borderRadius: 8,
              border: `1px solid ${BRAND.border}`,
              background: BRAND.panel,
              color: BRAND.text,
              fontSize: 14,
            }}
          />
          {suggestOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 52,
                marginTop: 6,
                width: 320,
                maxHeight: 280,
                overflowY: "auto",
                background: BRAND.panel,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 8,
                zIndex: 1000,
                boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
              }}
            >
              {suggestLoading ? (
                <div style={{ padding: 10, color: BRAND.muted, fontSize: 13 }}>Loading…</div>
              ) : filteredSuggestions.length === 0 ? (
                <div style={{ padding: 10, color: BRAND.muted, fontSize: 13 }}>No matches</div>
              ) : (
                filteredSuggestions.map((opt, i) => (
                  <div
                    key={opt.symbol}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setSuggestHighlight(i)}
                    onClick={() => pickSuggestion(i)}
                    style={{
                      padding: "10px 12px",
                      cursor: "pointer",
                      display: "flex",
                      gap: 10,
                      background: i === suggestHighlight ? "rgba(99,102,241,0.16)" : "transparent",
                    }}
                  >
                    <strong style={{ minWidth: 72 }}>{opt.symbol}</strong>
                    <span style={{ color: BRAND.textSecondary, fontSize: 13 }}>{opt.name}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </label>
        <button
          type="button"
          onClick={loadData}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            background: BRAND.accent,
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Load
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: "10px 14px",
            marginBottom: 16,
            borderRadius: 8,
            background: "rgba(239,68,68,0.15)",
            color: "#fca5a5",
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      <section style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: BRAND.text }}>
          Correlation with price (choose features for the model)
        </h2>
        <p style={{ fontSize: 13, color: BRAND.muted, marginBottom: 12 }}>
          Same view as the Fundamentals tab. Check &quot;Use&quot; to include a metric as an input feature for
          the LightGBM model.
        </p>
        {correlationsLoading ? (
          <div style={{ color: BRAND.muted, fontSize: 14 }}>Calculating correlations…</div>
        ) : !correlations.length ? (
          <div style={{ color: BRAND.muted, fontSize: 14 }}>Load a symbol to see correlations.</div>
        ) : (
          <div style={{ overflowX: "auto", border: `1px solid ${BRAND.border}`, borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: `2px solid ${BRAND.border}`, fontWeight: 600 }}>
                    Fundamental
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "10px 12px",
                      borderBottom: `2px solid ${BRAND.border}`,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onClick={() => setSortBy("all")}
                    title="Sort by All"
                  >
                    All {sortBy === "all" ? "▼" : ""}
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "10px 12px",
                      borderBottom: `2px solid ${BRAND.border}`,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onClick={() => setSortBy("ten")}
                    title="Sort by 10y"
                  >
                    10y {sortBy === "ten" ? "▼" : ""}
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "10px 12px",
                      borderBottom: `2px solid ${BRAND.border}`,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onClick={() => setSortBy("five")}
                    title="Sort by 5y"
                  >
                    5y {sortBy === "five" ? "▼" : ""}
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "10px 12px",
                      borderBottom: `2px solid ${BRAND.border}`,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onClick={() => setSortBy("three")}
                    title="Sort by 3y"
                  >
                    3y {sortBy === "three" ? "▼" : ""}
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "10px 12px",
                      borderBottom: `2px solid ${BRAND.border}`,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onClick={() => setSortBy("one")}
                    title="Sort by 1y"
                  >
                    1y {sortBy === "one" ? "▼" : ""}
                  </th>
                  <th style={{ textAlign: "center", padding: "10px 12px", borderBottom: `2px solid ${BRAND.border}`, fontWeight: 600 }}>
                    Use
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCorrelations.map((row) => (
                  <tr key={row.metricKey} style={{ borderBottom: `1px solid ${BRAND.border}` }}>
                    <td style={{ padding: "8px 12px" }}>{row.label}</td>
                    <td style={{ textAlign: "right", padding: "8px 12px" }}>
                      {row.all != null ? (row.all * 100).toFixed(1) + "%" : "—"}
                    </td>
                    <td style={{ textAlign: "right", padding: "8px 12px" }}>
                      {row.ten != null ? (row.ten * 100).toFixed(1) + "%" : "—"}
                    </td>
                    <td style={{ textAlign: "right", padding: "8px 12px" }}>
                      {row.five != null ? (row.five * 100).toFixed(1) + "%" : "—"}
                    </td>
                    <td style={{ textAlign: "right", padding: "8px 12px" }}>
                      {row.three != null ? (row.three * 100).toFixed(1) + "%" : "—"}
                    </td>
                    <td style={{ textAlign: "right", padding: "8px 12px" }}>
                      {row.one != null ? (row.one * 100).toFixed(1) + "%" : "—"}
                    </td>
                    <td style={{ textAlign: "center", padding: "8px 12px" }}>
                      <input
                        type="checkbox"
                        checked={selectedMetrics.has(row.metricKey)}
                        onChange={() => toggleMetric(row.metricKey)}
                        aria-label={`Use ${row.label} as feature`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div style={{ marginBottom: 24, display: "flex", gap: 10 }}>
        <button
          type="button"
          onClick={runPrediction}
          disabled={predictLoading || !symbol.trim() || selectedMetrics.size === 0}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: "none",
            background: selectedMetrics.size === 0 ? BRAND.muted : BRAND.accent,
            color: "#fff",
            fontWeight: 600,
            cursor: selectedMetrics.size === 0 ? "not-allowed" : "pointer",
          }}
        >
          {predictLoading ? "Running…" : "Run LightGBM prediction"}
        </button>
        <button
          type="button"
          onClick={runRegression}
          disabled={predictLoading || !symbol.trim() || selectedMetrics.size === 0}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: `1px solid ${BRAND.accent}`,
            background: "transparent",
            color: BRAND.accent,
            fontWeight: 600,
            cursor: selectedMetrics.size === 0 ? "not-allowed" : "pointer",
          }}
        >
          {predictLoading ? "Running…" : "Run Regression (1y)"}
        </button>
        {selectedMetrics.size === 0 && (
          <span style={{ marginLeft: 12, fontSize: 13, color: BRAND.muted }}>
            Select at least one fundamental above.
          </span>
        )}
      </div>

      {predictionResult && (
        <section style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: BRAND.text }}>
            Predicted probability of price change
          </h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
            {HORIZONS.map(({ key, label }) => {
              const probs = predictionResult[key] || {};
              const entries = Object.entries(probs).filter(([, v]) => v != null);
              return (
                <div
                  key={key}
                  style={{
                    flex: "1 1 240px",
                    minWidth: 200,
                    padding: 16,
                    borderRadius: 8,
                    border: `1px solid ${BRAND.border}`,
                    background: BRAND.panel,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 10, color: BRAND.accent }}>{label}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {entries.length ? (
                      entries.map(([bucket, pct]) => {
                        const pctNum = typeof pct === "number" ? pct : 0;
                        return (
                          <div
                            key={bucket}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 8,
                              fontSize: 13,
                            }}
                          >
                            <span style={{ color: BRAND.textSecondary }}>{bucket}</span>
                            <span style={{ fontWeight: 500 }}>{(pctNum * 100).toFixed(1)}%</span>
                            <div
                              style={{
                                width: 80,
                                height: 8,
                                borderRadius: 4,
                                background: BRAND.border,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  width: `${Math.min(100, pctNum * 100)}%`,
                                  height: "100%",
                                  background: pctNum > 0 ? BRAND.accent : "transparent",
                                  borderRadius: 4,
                                }}
                              />
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <span style={{ color: BRAND.muted, fontSize: 13 }}>No data</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section style={{ marginTop: 28 }}>
        <h2
          style={{
            fontSize: 16,
            fontWeight: 600,
            marginBottom: 10,
            color: BRAND.text,
            display: "flex",
            alignItems: "center",
            gap: 10,
            paddingLeft: "2ch",
          }}
        >
          {companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt=""
              style={companyLogoTitleStyle(COMPANY_LOGO_TITLE_SIZE)}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : null}
          <span>
            {(symbol || "").trim().toUpperCase() || "—"} — Regression: price, backtest, and 1-year prediction
          </span>
        </h2>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => setIsLog((v) => !v)}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: `1px solid ${BRAND.border}`,
              background: BRAND.panel,
              color: BRAND.textSecondary,
              fontSize: 12,
              cursor: "pointer",
            }}
            title="Toggle logarithmic price scale"
          >
            Scale: {isLog ? "Log" : "Linear"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 18, alignItems: "center", marginBottom: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: BRAND.textSecondary }}>
            <input
              type="radio"
              name="reg-mode"
              checked={!usePriceAction}
              onChange={() => setUsePriceAction(false)}
            />
            Fundamentals only (stair-step)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: BRAND.textSecondary }}>
            <input
              type="radio"
              name="reg-mode"
              checked={usePriceAction}
              onChange={() => setUsePriceAction(true)}
            />
            Fundamentals + price action (daily variation)
          </label>
        </div>
        <p style={{ fontSize: 13, color: BRAND.muted, marginBottom: 10 }}>
          Expanding-window yearly backtest: each year is predicted using only prior years. Forecast extends one trading year.
        </p>
        <div ref={chartWrapRef} style={{ width: "100%", border: `1px solid ${BRAND.border}`, borderRadius: 8 }} />
        {!regressionResult && (
          <div style={{ marginTop: 8, fontSize: 12, color: BRAND.muted }}>
            Historical price is shown. Click <strong>Run Regression (1y)</strong> to overlay backtest predictions and forecast with +/- 1 std bands.
          </div>
        )}
      </section>

      <p
        style={{
          marginTop: 32,
          fontSize: 12,
          color: BRAND.muted,
          maxWidth: 640,
        }}
      >
        For research and education only. This is not investment advice. Past correlations and model
        performance do not guarantee future results. The model uses only fundamentals, price, and date
        as inputs; indicators may be added later.
      </p>
    </div>
  );
}
